import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, ArrowRightLeft, Trash2, Wallet, Landmark, CreditCard } from 'lucide-react'
import { accountService } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatRupees } from '../utils/currency'

const accountTypes = [
  'cash',
  'bank',
  'card',
  'upi',
  'wallet',
  'investment',
  'other',
]

const AccountsPage = () => {
  const [loading, setLoading] = useState(true)
  const [accounts, setAccounts] = useState([])
  const [summary, setSummary] = useState({ total_balance: 0, by_type: {}, count: 0 })
  const [transactions, setTransactions] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)

  const [accountForm, setAccountForm] = useState({
    name: '',
    type: 'bank',
    currency: 'INR',
    balance: '',
    notes: '',
    is_default: false,
  })

  const [transferForm, setTransferForm] = useState({
    from_account_id: '',
    to_account_id: '',
    amount: '',
    description: 'Internal transfer',
  })

  const accountsById = useMemo(() => {
    return accounts.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }, [accounts])

  const loadData = async () => {
    try {
      setLoading(true)
      const [accountData, txData] = await Promise.all([
        accountService.getAccounts(),
        accountService.getTransactions(),
      ])

      setAccounts(accountData.accounts || [])
      setSummary(accountData.summary || { total_balance: 0, by_type: {}, count: 0 })
      setTransactions(txData || [])
    } catch (error) {
      toast.error(error.message || 'Failed to load accounts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleCreateAccount = async (e) => {
    e.preventDefault()

    try {
      await accountService.createAccount({
        ...accountForm,
        balance: Number(accountForm.balance || 0),
      })
      toast.success('Account created successfully')
      setShowAddModal(false)
      setAccountForm({
        name: '',
        type: 'bank',
        currency: 'INR',
        balance: '',
        notes: '',
        is_default: false,
      })
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to create account')
    }
  }

  const handleArchiveAccount = async (accountId) => {
    if (!window.confirm('Archive this account? Transactions remain intact.')) {
      return
    }

    try {
      await accountService.deleteAccount(accountId)
      toast.success('Account archived')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to archive account')
    }
  }

  const handleSetDefault = async (accountId) => {
    try {
      await accountService.updateAccount(accountId, { is_default: true })
      toast.success('Default account updated')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to set default account')
    }
  }

  const handleTransfer = async (e) => {
    e.preventDefault()

    try {
      await accountService.transferAccounts({
        ...transferForm,
        amount: Number(transferForm.amount),
      })
      toast.success('Transfer completed')
      setShowTransferModal(false)
      setTransferForm({
        from_account_id: '',
        to_account_id: '',
        amount: '',
        description: 'Internal transfer',
      })
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to transfer')
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading accounts..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Accounts</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage wallets, bank balances, transfers, and account history.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowTransferModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowRightLeft className="h-4 w-4" />
            Transfer
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Account
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Balance</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatRupees(summary.total_balance || 0)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Accounts</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.count || accounts.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Top Account Type</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
            {Object.entries(summary.by_type || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
          </p>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account List</h2>
        {accounts.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No accounts yet. Add your first account.</p>
        ) : (
          <div className="space-y-3">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-700"
              >
                <div>
                  <div className="flex items-center gap-2">
                    {account.type === 'cash' && <Wallet className="h-4 w-4 text-yellow-500" />}
                    {account.type === 'bank' && <Landmark className="h-4 w-4 text-blue-500" />}
                    {account.type === 'card' && <CreditCard className="h-4 w-4 text-purple-500" />}
                    {!['cash', 'bank', 'card'].includes(account.type) && <Wallet className="h-4 w-4 text-gray-500" />}
                    <p className="font-semibold text-gray-900 dark:text-white">{account.name}</p>
                    {account.is_default && (
                      <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                    {account.type} • {account.currency || 'INR'}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <p className="font-bold text-gray-900 dark:text-white">{formatRupees(account.balance)}</p>
                  {!account.is_default && (
                    <button
                      onClick={() => handleSetDefault(account.id)}
                      className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                    >
                      Set Default
                    </button>
                  )}
                  <button
                    onClick={() => handleArchiveAccount(account.id)}
                    className="text-red-600 hover:text-red-700"
                    title="Archive account"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Account Transactions</h2>
        {transactions.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No account transactions yet.</p>
        ) : (
          <div className="space-y-2">
            {transactions.slice(0, 15).map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between text-sm p-2 rounded bg-gray-50 dark:bg-gray-700"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">{item.type.replace('_', ' ')}</p>
                  <p className="text-gray-500 dark:text-gray-400">{item.description || 'No description'}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900 dark:text-white">{formatRupees(item.amount)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {item.account_id ? accountsById[item.account_id]?.name || 'Account' : 'Account'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Create Account</h3>
            <form onSubmit={handleCreateAccount} className="space-y-4">
              <input
                className="input-field"
                placeholder="Account name"
                value={accountForm.name}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />

              <select
                className="input-field"
                value={accountForm.type}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                {accountTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <input
                className="input-field"
                type="number"
                step="0.01"
                min="0"
                placeholder="Opening balance"
                value={accountForm.balance}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, balance: e.target.value }))}
              />

              <textarea
                className="input-field"
                rows={3}
                placeholder="Notes (optional)"
                value={accountForm.notes}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, notes: e.target.value }))}
              />

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={accountForm.is_default}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, is_default: e.target.checked }))}
                />
                Make this default account
              </label>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Transfer Between Accounts</h3>
            <form onSubmit={handleTransfer} className="space-y-4">
              <select
                className="input-field"
                value={transferForm.from_account_id}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, from_account_id: e.target.value }))}
                required
              >
                <option value="">From account</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name} ({formatRupees(acc.balance)})</option>
                ))}
              </select>

              <select
                className="input-field"
                value={transferForm.to_account_id}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, to_account_id: e.target.value }))}
                required
              >
                <option value="">To account</option>
                {accounts
                  .filter((acc) => acc.id !== transferForm.from_account_id)
                  .map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.name} ({formatRupees(acc.balance)})</option>
                  ))}
              </select>

              <input
                className="input-field"
                type="number"
                step="0.01"
                min="0"
                placeholder="Transfer amount"
                value={transferForm.amount}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, amount: e.target.value }))}
                required
              />

              <input
                className="input-field"
                placeholder="Description"
                value={transferForm.description}
                onChange={(e) => setTransferForm((prev) => ({ ...prev, description: e.target.value }))}
              />

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">Transfer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountsPage
